import type { input } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, TextField } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SelectOption } from '../../../@types';
import { CasinoGameRow } from '../../../@types/casino';
import { QUERY_KEYS } from '../../../constants';
import API_ROUTES from '../../../constants/apiRoutes';
import { useModal } from '../../../contexts/ModalContext';
import useMutateData from '../../../hooks/useMutateData';
import { useCasinoCategories } from '../../../queries/casino';
import { CasinoGameFormSchemaData, casinoGameFormSchema } from '../../../schema';
import FormModalLayout from '../../layouts/FormModalLayout';
import FormSelect from '../../molecules/FormSelect';

const AVAILABILITY_OPTIONS: SelectOption[] = [
  { id: 'true', name: 'Yes' },
  { id: 'false', name: 'No' },
];

const styles = {
  uploadButton: {
    alignSelf: 'flex-start',
  },
  imagePreview: {
    width: '100%',
    aspectRatio: '16 / 9',
    objectFit: 'cover',
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
  },
};

const toBooleanString = (value?: boolean | string) => {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';

  return 'false';
};

const getGameImageUrl = (game?: CasinoGameRow) => game?.imageURL ?? game?.imageUrl ?? '';

const createGamePayload = (data: CasinoGameFormSchemaData, existingGame?: CasinoGameRow) => {
  const formData = new FormData();

  formData.append('name', data.name ?? '');
  formData.append('categoryId', data.categoryId ?? '');
  formData.append('demoAvailable', data.demoAvailable);
  formData.append('frbAvailable', data.frbAvailable);

  if (data.image instanceof File) {
    formData.append('image', data.image);
  }

  if (existingGame?.position != null) {
    formData.append('position', String(existingGame.position));
  }

  if (existingGame?.providerGamePosition != null) {
    formData.append('providerGamePosition', String(existingGame.providerGamePosition));
  }

  return formData;
};

const CasinoGameForm = ({ closeModal }: { closeModal?: () => void }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { item } = useModal<CasinoGameRow>();
  const { data: categories = [], isLoading: categoriesLoading } = useCasinoCategories();
  const [previewUrl, setPreviewUrl] = useState(getGameImageUrl(item));

  const { updateData } = useMutateData(API_ROUTES.CASINO.GAME_UPDATE, [QUERY_KEYS.casinoGames], 'casino');

  const categoryOptions = useMemo<SelectOption[]>(
    () =>
      categories.map((category) => ({
        id: category._id,
        name: category.name,
      })),
    [categories]
  );

  const defaultValues = useMemo<CasinoGameFormSchemaData>(
    () => ({
      image: getGameImageUrl(item),
      name: item?.name ?? '',
      categoryId: item?.categoryId ?? '',
      demoAvailable: toBooleanString(item?.demoAvailable),
      frbAvailable: toBooleanString(item?.frbAvailable),
    }),
    [item]
  );

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
  } = useForm<input<typeof casinoGameFormSchema>, unknown, CasinoGameFormSchemaData>({
    defaultValues,
    resolver: zodResolver(casinoGameFormSchema),
  });

  useEffect(() => {
    reset(defaultValues);
    setPreviewUrl(getGameImageUrl(item));
  }, [defaultValues, item, reset]);

  const handleClose = () => {
    reset(defaultValues);
    closeModal?.();
  };

  useEffect(() => {
    return () => {
      if (previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];

    if (!file) return;

    setValue('image', file, { shouldDirty: true });
    setPreviewUrl(URL.createObjectURL(file));
  };

  const onSubmit = (data: CasinoGameFormSchemaData) => {
    if (!item) return;

    updateData(item.id, createGamePayload(data, item), handleClose, 'Game updated successfully.');
  };

  return (
    <FormModalLayout
      onSave={handleSubmit(onSubmit)}
      label='Edit game'
      onClose={handleClose}
      isEdit
      showEditTabs={false}
      disableSubmit={!isDirty}
    >
      <TextField label='Name' {...register('name')} error={!!errors.name} helperText={errors.name?.message} required />
      <FormSelect
        control={control}
        name='categoryId'
        label='Category'
        options={categoryOptions}
        error={errors.categoryId}
        disabled={categoriesLoading && !categoryOptions.length}
        required
      />
      <FormSelect
        control={control}
        name='demoAvailable'
        label='Demo Available'
        options={AVAILABILITY_OPTIONS}
        error={errors.demoAvailable}
        allowDeselect={false}
        required
      />
      <FormSelect
        control={control}
        name='frbAvailable'
        label='Frb Available'
        options={AVAILABILITY_OPTIONS}
        error={errors.frbAvailable}
        allowDeselect={false}
        required
      />
      <Button
        variant='contained'
        color='warning'
        startIcon={<CloudUploadIcon />}
        onClick={() => fileInputRef.current?.click()}
        sx={styles.uploadButton}
      >
        Upload image
      </Button>
      <input ref={fileInputRef} type='file' accept='.png,.jpg,.jpeg' hidden onChange={handleFileChange} />
      {previewUrl && (
        <Box
          component='img'
          src={previewUrl}
          alt={item?.name ? `${item.name} preview` : 'Game preview'}
          sx={styles.imagePreview}
        />
      )}
    </FormModalLayout>
  );
};

export default CasinoGameForm;
